import React, { useState, useEffect, useCallback } from "react";
import { getLignesSaisie, getLignesAjoutables, getFeuilleTemps, saisirFeuilleTemps } from "../services/api";
import ModaleV2 from "../components/ModaleV2";

// ===================================================================
// The Crowd v2 — Saisie des temps
//
// Adaptation v2 de pages/SaisieTemps.js : meme charge utile, memes
// regles (5 jours ouvres, pas de 0,5 h, une cellule deja enregistree
// reste verrouillee).
//
// Deux corrections par rapport a l'ancienne page :
// - Semaine et annee suivent la numerotation ISO. La navigation passe
//   d'une annee a l'autre (semaine 1 -> derniere semaine de l'annee
//   precedente, et inversement), au lieu de bloquer entre 1 et 53 sur
//   l'annee civile en cours. L'annee envoyee au backend est l'annee
//   ISO de la semaine.
// - Les erreurs de chargement (lignes, feuille) sont affichees au lieu
//   de partir silencieusement dans la console.
//
// Refonte du module Projets : les etapes de projet sont des lignes de
// saisie. Le backend compose la grille de la semaine (getLignesSaisie) :
// - taches du profil ;
// - lignes deja saisies cette semaine-la, etape comprise, meme terminees
//   depuis (saisissable = false : valeurs visibles, plus de nouvelle saisie).
// Aucune etape n'est affichee d'office (octobre 2026) : « Ajouter une ligne
// projet » propose les etapes des projets dont on est acteur principal ou
// secondaire. La ligne vaut pour la semaine affichee ; elle reapparait les
// semaines suivantes seulement si des heures y ont ete enregistrees.
// A l'enregistrement, le backend controle le role et le statut de chaque
// etape.
// ===================================================================

const CATEGORIE_ETAPE = "Etape projet";

// Semaine ISO d'une date : { annee, semaine } (l'annee ISO peut differer
// de l'annee civile autour du 1er janvier)
function semaineIso(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const jour = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - jour);
  const debutAnnee = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return {
    annee: date.getUTCFullYear(),
    semaine: Math.ceil(((date - debutAnnee) / 86400000 + 1) / 7),
  };
}

// Nombre de semaines ISO d'une annee (52 ou 53) : le 28 decembre est
// toujours dans la derniere semaine
function nbSemainesIso(annee) {
  return semaineIso(new Date(annee, 11, 28)).semaine;
}

// Lundi a vendredi de la semaine ISO, au format AAAA-MM-JJ
function joursDeLaSemaine(annee, semaine) {
  const jan4 = new Date(Date.UTC(annee, 0, 4));
  const lundiSemaine1 = new Date(jan4);
  lundiSemaine1.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() || 7) - 1));
  const lundi = new Date(lundiSemaine1);
  lundi.setUTCDate(lundiSemaine1.getUTCDate() + (semaine - 1) * 7);
  const jours = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date(lundi);
    d.setUTCDate(lundi.getUTCDate() + i);
    jours.push(d.toISOString().split("T")[0]);
  }
  return jours;
}

function formatJour(dateStr) {
  const d = new Date(dateStr + "T00:00:00Z");
  const noms = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
  return `${noms[d.getUTCDay()]} ${d.getUTCDate()}`;
}

function formatPeriode(jours) {
  const opts = { day: "numeric", month: "short", timeZone: "UTC" };
  const debut = new Date(jours[0] + "T00:00:00Z").toLocaleDateString("fr-FR", opts);
  const fin = new Date(jours[jours.length - 1] + "T00:00:00Z").toLocaleDateString("fr-FR", opts);
  return `${debut} – ${fin}`;
}

function dateJour(e) {
  return typeof e.date_jour === "string" ? e.date_jour.split("T")[0] : e.date_jour;
}

function formatEcheance(date) {
  if (!date) return null;
  const d = new Date(String(date).split("T")[0] + "T00:00:00Z");
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short", timeZone: "UTC" });
}

const estEtape = (l) => l.categorie === CATEGORIE_ETAPE;

// Taches d'abord, puis etapes regroupees par projet
function trierLignes(lignes) {
  return [...lignes].sort((a, b) => {
    const ea = estEtape(a) ? 1 : 0;
    const eb = estEtape(b) ? 1 : 0;
    if (ea !== eb) return ea - eb;
    return (a.projet_nom || "").localeCompare(b.projet_nom || "", "fr")
      || (a.categorie || "").localeCompare(b.categorie || "", "fr")
      || (a.nom || "").localeCompare(b.nom || "", "fr");
  });
}

function titreLigneFermee(l) {
  return estEtape(l)
    ? "Étape terminée ou désactivée : elle ne reçoit plus de temps."
    : "Tâche désactivée : elle ne reçoit plus de temps.";
}

// --- Modale « Ajouter une ligne » ---

function ModaleAjoutLignes({ etapes, onAjouter, onClose }) {
  const [selection, setSelection] = useState([]);

  const parProjet = etapes.reduce((acc, e) => {
    (acc[e.projet_nom] = acc[e.projet_nom] || []).push(e);
    return acc;
  }, {});

  const basculer = (id) =>
    setSelection((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <ModaleV2
      titre="Ajouter une ligne projet"
      onClose={onClose}
      actions={
        <>
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={onClose}>
            Annuler
          </button>
          <button
            type="button"
            className="v2-btn v2-btn-principal"
            disabled={selection.length === 0}
            onClick={() => onAjouter(etapes.filter((e) => selection.includes(e.tache_id)))}
          >
            Ajouter{selection.length > 1 ? ` (${selection.length})` : ""}
          </button>
        </>
      }
    >
      <p className="v2-champ-aide">
        Étapes des projets où vous êtes acteur principal ou secondaire. La ligne vaut pour la semaine
        affichée ; elle reste dans la grille si vous y saisissez des heures.
      </p>
      {Object.entries(parProjet).map(([projet, liste]) => (
        <fieldset key={projet} className="v2-groupe-champs">
          <legend className="v2-label">{projet}</legend>
          {liste.map((e) => (
            <label key={e.tache_id} className="v2-case-simple">
              <input
                type="checkbox"
                checked={selection.includes(e.tache_id)}
                onChange={() => basculer(e.tache_id)}
              />
              <span>
                {e.nom}
                {e.mon_role === "secondaire" && <span className="v2-champ-aide"> — en appui</span>}
                {e.date_fin_prevue && (
                  <span className="v2-champ-aide"> — échéance {formatEcheance(e.date_fin_prevue)}</span>
                )}
              </span>
            </label>
          ))}
        </fieldset>
      ))}
    </ModaleV2>
  );
}

// --- Page ---

function SaisieTempsV2({ collaborateur }) {
  const [periode, setPeriode] = useState(() => semaineIso(new Date())); // { annee, semaine }
  const [lignes, setLignes] = useState([]);
  const [ajoutables, setAjoutables] = useState([]);
  const [ajoutees, setAjoutees] = useState([]); // lignes ajoutees pour la semaine affichee
  const [modaleAjout, setModaleAjout] = useState(false);
  const [saisie, setSaisie] = useState({});
  const [existant, setExistant] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { type: "succes" | "erreur", texte }
  const [erreurLignes, setErreurLignes] = useState(null);
  const [erreurFeuille, setErreurFeuille] = useState(null);

  const { annee, semaine } = periode;
  const jours = joursDeLaSemaine(annee, semaine);
  const courante = semaineIso(new Date());
  const estCourante = courante.annee === annee && courante.semaine === semaine;

  // Etapes proposables par « Ajouter une ligne » : ne dependent pas de la semaine
  useEffect(() => {
    if (!collaborateur) return;
    getLignesAjoutables(collaborateur.id)
      .then(setAjoutables)
      .catch(() => setAjoutables([]));
  }, [collaborateur]);

  const chargerLignes = useCallback(() => {
    if (!collaborateur) return Promise.resolve();
    setErreurLignes(null);
    return getLignesSaisie(collaborateur.id, annee, semaine)
      .then(setLignes)
      .catch((err) => {
        setLignes([]);
        setErreurLignes(`Lignes de saisie non chargées : ${err.message}`);
      });
  }, [collaborateur, annee, semaine]);

  const chargerFeuille = useCallback(() => {
    if (!collaborateur) return;
    setErreurFeuille(null);
    getFeuilleTemps(collaborateur.id, annee, semaine)
      .then(setExistant)
      .catch((err) => {
        setExistant([]);
        setErreurFeuille(`Feuille de temps non chargée : ${err.message}`);
      });
  }, [collaborateur, annee, semaine]);

  // Changement de semaine : les lignes ajoutees ne valent que pour la semaine affichee
  useEffect(() => {
    setMessage(null);
    setAjoutees([]);
    chargerLignes();
    chargerFeuille();
  }, [chargerLignes, chargerFeuille]);

  useEffect(() => {
    const grille = {};
    existant.forEach((e) => {
      grille[`${e.tache_id}_${dateJour(e)}`] = e.heures;
    });
    setSaisie(grille);
  }, [existant]);

  if (!collaborateur) {
    return (
      <div className="v2-page v2-page-module">
        <div className="v2-msg v2-msg-erreur">Compte non configuré. Contactez votre manager.</div>
      </div>
    );
  }

  const idsGrille = new Set(lignes.map((l) => l.tache_id));
  const lignesAffichees = trierLignes([
    ...lignes,
    ...ajoutees.filter((a) => !idsGrille.has(a.tache_id)),
  ]);
  const idsAffiches = new Set(lignesAffichees.map((l) => l.tache_id));
  const etapesDisponibles = ajoutables.filter((a) => !idsAffiches.has(a.tache_id));

  const estVerrouillee = (tacheId, jour) =>
    existant.some((e) => e.tache_id === tacheId && dateJour(e) === jour);

  const semainePrecedente = () =>
    setPeriode(({ annee: a, semaine: s }) =>
      s > 1 ? { annee: a, semaine: s - 1 } : { annee: a - 1, semaine: nbSemainesIso(a - 1) }
    );

  const semaineSuivante = () =>
    setPeriode(({ annee: a, semaine: s }) =>
      s < nbSemainesIso(a) ? { annee: a, semaine: s + 1 } : { annee: a + 1, semaine: 1 }
    );

  const handleChange = (ligne, jour, value) => {
    if (!ligne.saisissable || estVerrouillee(ligne.tache_id, jour)) return;
    setSaisie((prev) => ({
      ...prev,
      [`${ligne.tache_id}_${jour}`]: value === "" ? 0 : parseFloat(value),
    }));
  };

  const ajouterLignes = (etapes) => {
    setAjoutees((prev) => [
      ...prev,
      ...etapes.map((e) => ({ ...e, origine: "ajout", saisissable: true })),
    ]);
    setModaleAjout(false);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const saisies = jours.map((jour) => ({
        collaborateur_id: collaborateur.id,
        date_jour: jour,
        semaine,
        annee,
        lignes: lignesAffichees
          .map((l) => ({ tache_id: l.tache_id, heures: saisie[`${l.tache_id}_${jour}`] || 0 }))
          .filter((l) => l.heures > 0),
      }));

      const result = await saisirFeuilleTemps({
        collaborateur_id: collaborateur.id,
        semaine,
        annee,
        saisies,
      });

      setMessage({
        type: "succes",
        texte: `Enregistré : ${result.inserted} lignes ajoutées, ${result.skipped} déjà saisies.`,
      });

      const updated = await getFeuilleTemps(collaborateur.id, annee, semaine);
      setExistant(updated);
      await chargerLignes();
    } catch (err) {
      setMessage({ type: "erreur", texte: `Erreur : ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const totalJour = (jour) =>
    lignesAffichees.reduce((sum, l) => sum + (saisie[`${l.tache_id}_${jour}`] || 0), 0);
  const totalLigne = (tacheId) => jours.reduce((sum, j) => sum + (saisie[`${tacheId}_${j}`] || 0), 0);
  const totalSemaine = jours.reduce((sum, j) => sum + totalJour(j), 0);
  const indexPremiereEtape = lignesAffichees.findIndex(estEtape);

  const renderLigne = (l) => (
    <tr key={l.tache_id} className={l.saisissable ? undefined : "v2-ligne-inactive"}>
      <td className="v2-saisie-tache" title={l.nom}>{l.nom}</td>
      <td className="v2-saisie-categorie" title={estEtape(l) ? l.projet_nom : l.categorie}>
        {estEtape(l) ? l.projet_nom : l.categorie}
        {l.origine === "ajout" && <> <span className="v2-statut">Ajoutée</span></>}
        {!l.saisissable && <> <span className="v2-statut">{estEtape(l) ? "Terminée" : "Inactive"}</span></>}
      </td>
      {jours.map((j) => {
        const verrouillee = estVerrouillee(l.tache_id, j);
        const fermee = !l.saisissable && !verrouillee;
        return (
          <td key={j} className="v2-col-jour">
            <input
              type="number"
              min="0"
              max="24"
              step="0.5"
              className={`v2-saisie-cellule${verrouillee || fermee ? " verrouillee" : ""}`}
              value={saisie[`${l.tache_id}_${j}`] || ""}
              onChange={(e) => handleChange(l, j, e.target.value)}
              disabled={verrouillee || fermee}
              aria-label={`${l.nom}, ${formatJour(j)}`}
              title={verrouillee ? "Déjà enregistré" : fermee ? titreLigneFermee(l) : undefined}
            />
          </td>
        );
      })}
      <td className="v2-col-total">{totalLigne(l.tache_id)} h</td>
    </tr>
  );

  return (
    <div className="v2-page v2-page-module">
      <header className="v2-module-entete">
        <h1 className="v2-module-titre">Saisie des temps</h1>
        <p className="v2-sous-titre">
          Temps passé par tâche et par étape de projet, du lundi au vendredi. Une saisie enregistrée n'est plus modifiable.
        </p>
      </header>

      <div className="v2-barre-outils">
        <div className="v2-semaine-nav">
          <button type="button" className="v2-btn v2-btn-secondaire v2-btn-icone" onClick={semainePrecedente} aria-label="Semaine précédente">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 3.5 5.5 8l4.5 4.5" /></svg>
          </button>
          <div className="v2-semaine-libelle">
            <span className="v2-semaine-titre">Semaine {semaine} — {annee}</span>
            <span className="v2-semaine-dates">{formatPeriode(jours)}</span>
          </div>
          <button type="button" className="v2-btn v2-btn-secondaire v2-btn-icone" onClick={semaineSuivante} aria-label="Semaine suivante">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 3.5 10.5 8 6 12.5" /></svg>
          </button>
          {!estCourante && (
            <button type="button" className="v2-btn v2-btn-secondaire" onClick={() => setPeriode(semaineIso(new Date()))}>
              Semaine en cours
            </button>
          )}
        </div>

        <div className="v2-barre-outils-droite">
          {etapesDisponibles.length > 0 && (
            <button type="button" className="v2-btn v2-btn-secondaire" onClick={() => setModaleAjout(true)}>
              Ajouter une ligne projet
            </button>
          )}
          <span className="v2-total-semaine">Total <strong>{totalSemaine} h</strong></span>
          <button type="button" className="v2-btn v2-btn-principal" onClick={handleSubmit} disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>

      {erreurLignes && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreurLignes}</div>}
      {erreurFeuille && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreurFeuille}</div>}
      {message && (
        <div className={`v2-msg ${message.type === "erreur" ? "v2-msg-erreur" : "v2-msg-succes"} v2-marge-bas`} role="status">
          {message.texte}
        </div>
      )}

      <div className="v2-panneau v2-tableau-conteneur">
        <table className="v2-tableau v2-saisie-tableau">
          <thead>
            <tr>
              <th scope="col" className="v2-col-tache">Tâche</th>
              <th scope="col" className="v2-col-categorie">Catégorie / projet</th>
              {jours.map((j) => (
                <th key={j} scope="col" className="v2-col-jour">{formatJour(j)}</th>
              ))}
              <th scope="col" className="v2-col-total">Total</th>
            </tr>
          </thead>
          <tbody>
            {lignesAffichees.length === 0 && (
              <tr>
                <td colSpan={jours.length + 3} className="v2-vide">
                  Aucune ligne de saisie : ni tâche associée à votre profil, ni étape de projet.
                </td>
              </tr>
            )}
            {lignesAffichees.map((l, i) => (
              <React.Fragment key={l.tache_id}>
                {i === indexPremiereEtape && (
                  <tr className="v2-saisie-section">
                    <th scope="colgroup" colSpan={jours.length + 3}>Étapes de projet</th>
                  </tr>
                )}
                {renderLigne(l)}
              </React.Fragment>
            ))}
          </tbody>
          {lignesAffichees.length > 0 && (
            <tfoot>
              <tr>
                <th scope="row" colSpan={2}>Total</th>
                {jours.map((j) => (
                  <td key={j} className="v2-col-jour">{totalJour(j)} h</td>
                ))}
                <td className="v2-col-total">{totalSemaine} h</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {modaleAjout && (
        <ModaleAjoutLignes
          etapes={etapesDisponibles}
          onAjouter={ajouterLignes}
          onClose={() => setModaleAjout(false)}
        />
      )}
    </div>
  );
}

export default SaisieTempsV2;
